import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { email } from '../utils/generalData.jsx';
import { media } from '../styles/breakpoints';
import StyledButton from "../components/ui/StyledButton";
import { getPaddleConfig, getRsoPriceId, getProductNameFromPaddleId } from '../config/paddle';
import { getProduct, getProductDownloads, getProductManual } from '../config/products';
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    FeatureItem as OriginalFeatureItem, // Rename to avoid conflict
    FeatureTitle,
    FeaturesGrid,
    HeroSection as BaseHeroSection,
    PageTitle,
    FigureWrapper, FigureCaption,
} from '../components/ui/PageElements';

import {
    AudienceItem, AudienceList,
    FeatureList,
    FeatureListItem,
    TechSpecsList, TechSpecItem
} from "../components/ui/ListStyles.jsx";
import AudioPlayer from '../components/AudioPlayer';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import PageMeta from '../components/PageMeta';

// Logo with explicit aspect ratio to prevent CLS
const Logo = styled.img`
    max-width: 300px;
    width: 300px;
    height: auto;
    display: block;
    margin: 0 auto;
    aspect-ratio: 300 / 228; /* Prevents layout shift while loading */
`;

// HeroSection - no min-height needed, images have explicit dimensions
const HeroSection = styled(BaseHeroSection)`
    /* Removed min-height as ResponsiveImage provides width/height preventing CLS */
`;

// Container for multiple paragraph SubHeadline content
const SubHeadlineContainer = styled.div`
    font-size: 18px;
    max-width: 700px;
    margin: 0 auto 40px;
    color: var(--text-muted);
    line-height: 1.6;

    p {
        margin-bottom: 20px;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

// Styled ResponsiveImage to remove border and background for RSO page
const FigureImage = styled(ResponsiveImage)`
    display: block;
    margin: 0 auto;
    border: none;
    background-color: transparent;
    max-height: 250px;
    object-fit: contain;

    picture {
        display: block;
        width: fit-content !important;
        margin: 0 auto;
    }

    img {
        display: block;
        margin: 0 auto;
        border: none;
        background-color: transparent;
        max-height: 250px;
        object-fit: contain;
    }
`;

// Hero image wrapper - simplified for LCP optimization
// Using plain div instead of styled component to reduce render delay
const HeroImageWrapper = styled.div`
    width: 100%;
    max-width: 675px;
    margin: 0 auto;

    img {
        width: 100%;
        height: auto;
        display: block;
        border: none;
        background-color: transparent;
    }
`;

// Larger variant for hero gif
const LargeFigureImage = styled(FigureImage)`
    max-height: 350px;

    img {
        max-height: 350px;
    }
`;

// Bordered variant for animated gif
const BorderedLargeFigureImage = styled(LargeFigureImage)`
    border: 2px solid rgba(255, 51, 153, 0.25);
    display: block;
    max-width: 350px;
    width: 100%;
    margin: 0 auto;

    picture {
        width: 100% !important;
    }

    img {
        display: block;
        width: 100%;
        height: auto;
    }
`;

// --- STYLES SPECIFIC TO THIS PAGE ---

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

// Accordion category card - maintains consistent size
// Includes button reset styles for semantic HTML when used with as="button"
const FeatureItem = styled(OriginalFeatureItem)`
    cursor: pointer;
    transition: border-color 0.3s ease, background-color 0.3s ease;
    border-color: ${({ $isActive }) => ($isActive ? 'var(--primary-color)' : 'inherit')};
    background-color: ${({ $isActive }) => ($isActive ? 'rgba(255, 51, 153, 0.08)' : 'rgba(15, 15, 25, 0.5)')};
    position: relative;
    margin-bottom: ${({ $isActive }) => ($isActive ? '0' : '8px')};
    border-radius: 0;

    /* Button reset for semantic HTML */
    font: inherit;
    text-align: left;
    width: 100%;

    /* Remove bullet points from summary descriptions */
    .feature-list-item {
        list-style: none;
        &::before {
            display: none;
        }
    }
`;

// Container for image + content layout in accordion summary cards
const FeatureContentLayout = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
    margin-bottom: 20px;
    overflow: hidden;

    ${media.sm} {
        display: block;
    }
`;

// Wrapper for thumbnail to enable centering
const ThumbnailWrapper = styled.div`
    flex: 1 1 200px;
    display: flex;
    justify-content: center;
    min-width: 120px;
    max-width: ${props => props.$fullWidth ? '100%' : '300px'};

    ${media.sm} {
        float: left;
        margin-right: 12px;
        margin-bottom: 8px;
        min-width: auto;
        max-width: none;
    }
`;

// Thumbnail image for accordion sections
const FeatureThumbnail = styled(ResponsiveImage)`
    width: ${props => props.$fullWidth ? 'auto' : '120px'};
    height: 120px;
    object-fit: ${props => props.$fullWidth ? 'contain' : 'cover'};
    border: none;
    background-color: transparent;
    max-width: ${props => props.$fullWidth ? '100%' : '120px'};

    img {
        width: ${props => props.$fullWidth ? 'auto' : '120px'};
        height: 120px;
        object-fit: ${props => props.$fullWidth ? 'contain' : 'cover'};
        border: none;
        background-color: transparent;
        max-width: ${props => props.$fullWidth ? '100%' : '120px'};
    }

    ${media.sm} {
        width: auto;
        height: 80px;
        max-width: none;

        img {
            width: auto;
            height: 80px;
            max-width: none;
        }
    }
`;

// Content wrapper for title + description
const FeatureTextContent = styled.div`
    flex: 999 1 auto;
    min-width: 200px;
    overflow-wrap: break-word;
    word-wrap: break-word;

    /* Align description text with top of image */
    ul {
        margin-top: 0;
    }

    ${media.sm} {
        min-width: 0;
        width: 100%;
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .fixed-button {
        width: 200px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px;
        padding: 0 !important;
        box-sizing: border-box;
    }
`;

const PurchaseDisclaimer = styled.p`
    font-size: 12px;
    color: #888;
    text-align: center;
    margin-top: 15px;
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;

    a {
        color: var(--text-muted);
        text-decoration: underline;
        text-underline-offset: 2px;
        &:hover {
            color: var(--primary-color);
        }
    }
`;

// Consent checkbox for purchase
const shakeAnimation = keyframes`
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
`;

const ConsentWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 10px;
    margin: 20px auto;
    max-width: 500px;
    padding: 15px;
    border: 1px solid ${props => props.$error ? 'var(--primary-color)' : 'var(--grid-color)'};
    background: ${props => props.$error ? 'rgba(255, 51, 153, 0.1)' : 'rgba(0, 0, 0, 0.3)'};
    transition: border-color 0.3s ease, background-color 0.3s ease;
    animation: ${props => props.$error ? css`${shakeAnimation} 0.4s ease` : 'none'};

    ${media.sm} {
        padding: 12px;
        gap: 8px;
    }
`;

const ConsentCheckbox = styled.input`
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: var(--primary-color);
    flex-shrink: 0;
`;

const ConsentLabel = styled.label`
    font-size: 12px;
    color: ${props => props.$error ? 'var(--primary-color)' : 'var(--text-muted)'};
    line-height: 1.5;
    cursor: pointer;
    transition: color 0.3s ease;

    a {
        color: ${props => props.$error ? 'var(--primary-color)' : 'var(--secondary-color)'};
        text-decoration: underline;
        text-underline-offset: 2px;
        &:hover {
            color: var(--primary-color);
        }
    }

    ${media.sm} {
        font-size: 11px;
    }
`;

// --- DOWNLOAD DROPDOWN COMPONENTS ---

const DownloadDropdown = styled.div`
    position: relative;
    display: inline-block;
`;

const DropdownTrigger = styled(StyledButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    /* Active state when dropdown is open */
    ${props => props.$isOpen && css`
        color: var(--background-color);
        &:before {
            transform: translateX(100%);
        }
    `}
`;

const DropdownArrow = styled.span`
    display: inline-block;
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    font-size: 10px;
`;

const DropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(15, 15, 25, 0.98);
    border: 1px solid var(--primary-color);
    min-width: 200px;
    z-index: 100;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transform: translateX(-50%) ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
    transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
`;

const DropdownItem = styled.a`
    display: block;
    padding: 12px 20px;
    color: var(--text-color);
    text-decoration: none;
    font-size: 14px;
    text-align: left;
    transition: background-color 0.2s ease, color 0.2s ease;
    border-bottom: 1px solid rgba(255, 51, 153, 0.2);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: rgba(255, 51, 153, 0.15);
        color: var(--primary-color);
    }
`;

// --- NEW ACCORDION ANIMATION LOGIC ---

const ANIMATION_DURATION = 300; // ms - faster for accordion

const slideInFromLeft = keyframes`
    from { transform: translateX(-30px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
`;

const slideInFromRight = keyframes`
    from { transform: translateX(30px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
`;

const slideOutToLeft = keyframes`
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(-30px); opacity: 0; }
`;

const slideOutToRight = keyframes`
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(30px); opacity: 0; }
`;

const slideInFromTop = keyframes`
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const slideOutToTop = keyframes`
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(-20px); opacity: 0; }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;


// Individual accordion section wrapper
const AccordionSection = styled.div`
    position: relative;
    margin-bottom: ${({ $isExpanded, $isLast }) => ($isExpanded ? '0' : $isLast ? '0' : '8px')};
`;

// Coming Soon card styling - slightly different from standard content cards
const ComingSoonFeatureItem = styled(OriginalFeatureItem)`
    border: 1px dashed rgba(255, 51, 153, 0.4);
    background: linear-gradient(135deg, rgba(15, 15, 25, 0.6) 0%, rgba(25, 15, 35, 0.4) 100%);
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-left: 60px solid transparent;
        border-top: 60px solid rgba(255, 51, 153, 0.15);
        z-index: 1;
    }
    
    &::after {
        content: 'COMING SOON';
        position: absolute;
        top: 8px;
        right: 8px;
        font-size: 10px;
        font-weight: bold;
        color: rgba(255, 51, 153, 0.8);
        letter-spacing: 0.5px;
        z-index: 2;
        transform: rotate(0deg);
    }
`;

const ComingSoonTitle = styled(FeatureTitle)`
    color: rgba(255, 255, 255, 0.85);
`;

// Sticky title that extends beyond the content wrapper
const StickyTitleContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(10, 10, 20, 0.98);
    backdrop-filter: blur(15px);
    border-bottom: 2px solid var(--primary-color);
    padding: 15px 40px;
    margin: 0;
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--primary-color);
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    /* Extend beyond parent padding to full accordion width */
    margin-left: -41px; /* -40px content padding - 1px border */
    margin-right: -41px;
    width: calc(100% + 82px); /* Compensate for negative margins + borders */
`;


// Content container - smooth animation with max-height
const DetailsContainer = styled.div`
    max-height: ${({ height }) => height === 0 ? '0px' : '10000px'};
    overflow: clip; /* Use clip instead of hidden to allow sticky positioning */
    transition: max-height ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1);
    background-color: ${({ height }) => height > 0 ? 'rgba(15, 15, 25, 0.8)' : 'transparent'};
    border: ${({ height }) => height > 0 ? '1px solid rgba(255, 51, 153, 0.2)' : 'none'};
    margin-bottom: ${({ height, $isLast }) => (height > 0 && !$isLast ? '8px' : '0')};
    position: relative;
`;

// Content wrapper - restore the nice padding
const ContentWrapper = styled.div`
    padding: 30px 40px;
    opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
    transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-10px')});
    transition: opacity ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1),
                transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1);
    /* Ensure sufficient height for scrolling and sticky behavior */
    min-height: 400px;
`;

const SummaryButton = styled(StyledButton)`
    margin-top: auto;
    padding-top: 15px;
    padding-bottom: 15px;
    width: 100%;
`;

// Image for the section tiles
const SectionTileImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    margin-bottom: 20px;
    border: none;
    background-color: transparent;
`;

const ExpandableGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 40px;
    /* Critical: no overflow properties that would break sticky positioning */
`;

// Aside link for manual reference
const AsideLink = styled.p`
    text-align: center;
    font-size: 14px;
    color: #888;
    margin: 0;

    a {
        color: inherit; /* Ensure it inherits color */
        text-decoration: underline;
        text-underline-offset: 2px;
        transition: color 0.3s ease;
        &:hover {
            color: var(--primary-color);
        }
    }
`;

// --- DEMO DATA ---
const rsoProduct = getProduct('rso');
const manualUrl = getProductManual('rso');
const demoDownloads = getProductDownloads('rso');
const antres808Url ="https://open.spotify.com/artist/7E33d5yg6DaIjIF1LmmmDH?si=mK4h--jlRBOnug3Cydu1wg";
const rsoAudioTracks = [
    { title: "RSO is free and unafraid to love", artist: "Antres 808", src: "/assets/RSO/sound/RSO is free and unafraid to love.mp3", artistUrl: antres808Url },
    { title: "RSO understands his scale within the universe, and it elevates him above you all", artist: "Antres 808", src: "/assets/RSO/sound/RSO understands his scale within the universe, and it elevates him above you all.mp3", artistUrl: antres808Url },
    { title: "RSO knows everything is already broken", artist: "Antres 808", src: "/assets/RSO/sound/RSO knows everything is already broken.mp3", artistUrl: antres808Url },
    { title: "RSO severely damaged the coda", artist: "Antres 808", src: "/assets/RSO/sound/RSO severely damaged the coda.mp3", artistUrl: antres808Url },
    { title: "RSO finds your gravity purely optional", artist: "Antres 808", src: "/assets/RSO/sound/RSO finds your gravity purely optional.mp3", artistUrl: antres808Url }
];

const sectionOrder = ['osc', 'sculpting', 'animation'];

// Helper function to parse description text and render Highlight components
const parseDescription = (text) => {
    const parts = text.split(/(<Highlight>.*?<\/Highlight>)/);
    return parts.map((part, index) => {
        if (part.startsWith('<Highlight>') && part.endsWith('</Highlight>')) {
            const content = part.replace(/<\/?Highlight>/g, '');
            return <Highlight key={index}>{content}</Highlight>;
        }
        return part;
    });
};

const SectionContent = ({ sectionName }) => {
    // We use the original, un-styled FeatureItem for the detail cards
    // to distinguish them from the active summary cards.
    const DetailFeatureItem = OriginalFeatureItem;

    switch(sectionName) {
        case 'osc':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>Quaternion Phase Oscillator</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>A <Highlight>chaotic oscillator</Highlight> with intuitive control. Three XY pads connect you directly to its behavior. <Highlight> Push it, explore it, perform with it</Highlight>. And despite the chaos, it stays musical. Plenty of sweet spots produce tonal, playable sounds.</FeatureListItem>
                            <FeatureListItem><Highlight>4D Chaos:</Highlight> Generates <Highlight>complex waveforms</Highlight> through chaotic oscillations in 4-dimensional space. Can be stable and repeating, or purely chaotic.</FeatureListItem>
                            <FeatureListItem><Highlight>The Edge of Chaos:</Highlight> From pure sine-like tones to atonal noise. <Highlight>Intricate and evolving textures</Highlight> emerge at the fragile boundary between order and chaos.</FeatureListItem>
                            <FeatureListItem><Highlight>Built-in Destruction: </Highlight> A bitcrusher and downsampler at the end of the signal path, for when <Highlight>chaos isn't enough</Highlight>.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/RSOsc.png" alt="Image of the Quaternion Phase oscillator" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>The chaotic beauty of the Quaternion Phase oscillator.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Super VPS + Lissajous Modulator</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>Rich, <Highlight>complex harmonic content</Highlight>. This engine excels at unique basses, complex leads, and evolving pads with intricate internal movement.</FeatureListItem>
                            <FeatureListItem><Highlight>Super VPS Core:</Highlight> Seven detunable Vector Phaseshaping oscillators stack together, with individual oscillators switchable to <Highlight>sub-octave mode</Highlight>. Combined with <Highlight>stereo-widening capabilities</Highlight>, this creates a fat, harmonically dense foundation.</FeatureListItem>
                            <FeatureListItem><Highlight>Lissajous FM/PM:</Highlight> A Lissajous modulator that can be switched from <Highlight>LFO to audio range</Highlight>, modulating the phase distortion vectors to produce a wide range of FM and phase modulation timbres, from evolving textures to aggressive tones.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/vps.png" alt="RSO interface showing Super VPS oscillator." sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>The rich harmonic potential of the Super VPS engine.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                </FeaturesGrid>
            );
        case 'sculpting':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>The Ladder Filter</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Classic 24dB Ladder Filter:</Highlight> A detailed virtual analog emulation capturing the renowned <Highlight>warmth and musical resonance</Highlight> of the legendary ladder circuit design.</FeatureListItem>
                            <FeatureListItem><Highlight>Classic Sound:</Highlight> Creamy low-pass filtering with smooth, <Highlight>musical resonance</Highlight>. Handles everything from subtle shaping to dramatic sweeps without getting harsh.</FeatureListItem>
                            <FeatureListItem><Highlight>Built-in Contour Envelope:</Highlight> Features a dedicated envelope generator that modulates the filter cutoff in response to note events. The contour amount can be <Highlight>reversed for negative modulation</Highlight>, offering more flexibility than traditional designs.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/ladder.png" alt="Close-up of the RSO ladder filter section." style={{ maxWidth: '150px'}} sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>The classic Ladder Filter.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>XPander Filter</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>A <Highlight>multimode filter</Highlight> inspired by the Oberheim Xpander. Reimagined with <Highlight>full stereo control</Highlight>, but keeping the smooth, musical character that defined an era.</FeatureListItem>
                            <FeatureListItem><Highlight>15 Filter Modes:</Highlight> Uses <Highlight>pole mixing</Highlight> to create 15 distinct response curves from low-pass to high-pass, including bandpass and unique combinations. User-customizable modes are possible through configuration files.</FeatureListItem>
                            <FeatureListItem><Highlight>Stereo Frequency Control:</Highlight> Independent left and right channel <Highlight>frequency and resonance control</Highlight> creates wide, evolving stereo textures and complex spatial filtering effects.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/Xpander.png" alt="Close-up of the XPander filter section." style={{ maxWidth: '150px'}} sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>The legendary XPander multi-mode filter.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>West Coast Channel</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>More than a Filter:</Highlight> A complete west coast processing channel featuring a <Highlight>Wavefolder</Highlight> followed by a <Highlight>Strikable Low Pass Gate</Highlight>.</FeatureListItem>
                            <FeatureListItem><Highlight>Classic Wavefolder:</Highlight> A virtual analog emulation of the Buchla 259's wavefolder. Enhanced with <Highlight>stereo asymmetric folding</Highlight>.</FeatureListItem>
                            <FeatureListItem><Highlight>Variable Low Pass Gate:</Highlight> Authentic low pass gate behavior with switchable <Highlight>12dB/24dB slope selection</Highlight> for traditional or darker response.</FeatureListItem>
                            <FeatureListItem><Highlight>Organic Sound Character:</Highlight> Delivers the signature <Highlight>plucky, percussive tones</Highlight> and rich harmonic content characteristic of west coast synthesis.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/WestCoast.png" alt="Close-up of the West Coast Channel." style={{ maxWidth: '150px'}} sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>West Coast synthesis in a box.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Envelope & Output Stage</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>The Final Stages:</Highlight> An ADSR envelope to shape your dynamics, and an output module to prepare the sound for the world.</FeatureListItem>
                            <FeatureListItem><Highlight>Continuous Curve Control:</Highlight> Morph the envelope shape from <Highlight>logarithmic through linear to exponential</Highlight>, without affecting duration.</FeatureListItem>
                            <FeatureListItem><Highlight>Stereo Output Stage:</Highlight> Complete with <Highlight>stereo balance control</Highlight> to adjust the left/right distribution of your sound.</FeatureListItem>
                            <FeatureListItem><Highlight>ADAA Soft Clipper:</Highlight> The output stage includes a bypassable <Highlight>high-quality soft clipper</Highlight> for tasteful harmonic saturation.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/envNout.png" alt="Close-up of the envelope ad out modules" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>Shape your dynamics with precision.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <ComingSoonFeatureItem>
                        <ComingSoonTitle>Polyvoks Filter</ComingSoonTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Soviet Chip Design:</Highlight> A faithful recreation of the legendary Soviet filter circuit, famous for its lack of buffering and <Highlight>unstable resonance</Highlight>.</FeatureListItem>
                            <FeatureListItem><Highlight>Aggressive Character:</Highlight> This is not a polite filter. It screams, it growls, and adds a distinctive <Highlight>metallic grit</Highlight> to anything passing through it.</FeatureListItem>
                            <FeatureListItem><Highlight>Dual Mode:</Highlight> Switchable between Low Pass and Band Pass modes, both delivering that signature <Highlight>raw, industrial sound</Highlight> that earned the Polyvoks its cult following.</FeatureListItem>
                        </FeatureList>
                    </ComingSoonFeatureItem>
                </FeaturesGrid>
            );
        case 'animation':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>The Modulation System</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Intuitive Workflow:</Highlight> Select a modulation source, drag a ring around any knob or pad to set the depth. Fast and Visual.</FeatureListItem>
                            <FeatureListItem><Highlight>Plenty of Sources:</Highlight> 19 modulation outputs across 5 modules. More than enough to cover all your modulation needs.</FeatureListItem>
                            <FeatureListItem><Highlight>Modulate Everything:</Highlight> Every parameter in RSO is a destination. Including the modulators themselves. Self and cross-modulation encouraged.</FeatureListItem>
                            <FeatureListItem><Highlight>Modulation Overlay:</Highlight> Visualize all your connections with RSO's optional overlay. Understand your patch in one glance.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/overlay.png" alt="image showing the modulation overlay" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>RSO's Modulation Overlay lets you see every connection</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Performance Hub & MPE Core</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>The Center of Expression:</Highlight> This module translates your physical actions into modulation. Velocity, aftertouch, mod wheel, pitchbend. All available as modulation sources.</FeatureListItem>
                            <FeatureListItem><Highlight>Full MPE Integration:</Highlight> Per-note Pressure, Slide, and Pitchbend make RSO a deeply <Highlight>expressive instrument</Highlight>. Standard MIDI with polyphonic aftertouch is also supported.</FeatureListItem>
                            <FeatureListItem><Highlight>Various Utilities:</Highlight> Features an octave switch, <Highlight>portamento control</Highlight> and a configurable <Highlight>AR envelope</Highlight> generated for every note played.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/inputs.png" alt="Screenshot of the 'Inputs' module" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>Connect your performance to the synth engine.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Wild Morphing LFOs</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Continuously Evolving Shapes:</Highlight> Two LFOs that morph smoothly between square, sine, and triangle. Symmetry control lets you <Highlight>skew any waveform</Highlight> for even more variation.</FeatureListItem>
                            <FeatureListItem><Highlight>Spice it Up:</Highlight> The "Spices" parameter <Highlight>randomizes</Highlight> the waveform's peak each cycle. Dial it in for subtle wobble, crank it for pure noise.</FeatureListItem>
                            <FeatureListItem><Highlight>Phase Offset Output:</Highlight> Each LFO has a secondary output with <Highlight>continuous phase offset</Highlight> control. Useful for stereo modulation or complex phasing relationships.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/lfos.png" alt="Close-up of our two morphing LFOs" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>These are not your average LFOs.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>PIM² Topographic Surveyor</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Unique Evolving Textures:</Highlight> Four probes placed on a moving 2D <Highlight>Perlin noise</Highlight> landscape, each generating its own modulation signal. Visualized on screen in real-time.</FeatureListItem>
                            <FeatureListItem><Highlight>Configurable Topography:</Highlight>  Shape the noise landscape with controls for <Highlight>contrast, speed, density</Highlight>, and more. Adjust the position of each probe independently.</FeatureListItem>
                            <FeatureListItem><Highlight>Musical & Organic:</Highlight> The probes share the same terrain, so their outputs are naturally related. Move them closer for <Highlight>correlated modulation</Highlight>, spread them apart for independence. Perfect for <Highlight>stereo movement</Highlight> or slowly evolving, non-repeating textures.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/PIM2.png" alt="Screenshot of the PIM² module" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>Survey a unique generative landscape.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Dual Slope Function Generator</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>West Coast Philosophy:</Highlight> A module built for <Highlight>exploration</Highlight>. Simple, interactive elements that lead to surprisingly complex results.</FeatureListItem>
                            <FeatureListItem><Highlight>Twin Slopes:</Highlight> Two identical A(H)R envelopes with continuous curve control. Each can loop, hold, or sustain. Link them together for chained, <Highlight>sequential behavior</Highlight> where one envelope leads into the next.</FeatureListItem>
                            <FeatureListItem><Highlight>Swiss Army Modulator:</Highlight> Two snappy AD envelopes, a complex multi-stage envelope, two looping LFOs with curve control, and much more. One module, <Highlight>endless possibilities</Highlight>.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/dslopes.png" alt="Close-up of the Dual Slope module" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>A playground for generating complex envelopes.</FigureCaption></FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Sync & Retrigger System</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Sync System:</Highlight> Both LFOs and the Dual Slope can be synced to <Highlight>tempo or keyboard</Highlight>. Choose your time division and let the modulators follow your music.</FeatureListItem>
                            <FeatureListItem><Highlight>Tempo Sync Modes:</Highlight> Two modes to choose from. <Highlight>Beat Retrigger</Highlight> resets the modulator on every beat division. <Highlight>Lock</Highlight> goes further, matching the entire cycle duration to your selected beat value. Both keep your modulation <Highlight>tight with the tempo</Highlight>.</FeatureListItem>
                            <FeatureListItem><Highlight>Keyboard Retrigger:</Highlight> Modulators can also be restarted on each new note.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper><FigureImage src="/assets/RSO/img/sync.png" alt="Close-up of the Sync menu" sizes="(max-width: 800px) 100vw, 800px" /><FigureCaption>Both LFOs and Slopes can be synced to the Keyboard or the Tempo</FigureCaption></FigureWrapper>

                    </DetailFeatureItem>
                </FeaturesGrid>
            );
        default:
            return null;
    }
}


// --- MAIN PAGE COMPONENT ---
const RsoPage = () => {
    const navigate = useNavigate();

    // Page-specific preload for LCP hero image
    useEffect(() => {
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.type = 'image/avif';
        preloadLink.href = '/assets/RSO/img/heroShot-800w.avif';
        preloadLink.imageSrcset = '/assets/RSO/img/heroShot-400w.avif 400w, /assets/RSO/img/heroShot-800w.avif 800w, /assets/RSO/img/heroShot-1200w.avif 1200w';
        preloadLink.imageSizes = '(max-width: 600px) 90vw, 675px';
        document.head.appendChild(preloadLink);

        return () => {
            document.head.removeChild(preloadLink);
        };
    }, []);

    const [activeSections, setActiveSections] = useState(new Set());

    // Consent checkbox state for purchase
    const [consentChecked, setConsentChecked] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    const consentRefTop = useRef(null);
    const consentRefBottom = useRef(null);

    // Demo download dropdown state
    const [demoDropdownOpen, setDemoDropdownOpen] = useState(false);

    // Paddle state
    const [paddleReady, setPaddleReady] = useState(false);
    const [formattedPrice, setFormattedPrice] = useState('$20'); // Default, updated by Paddle

    // Handle checkout completion - redirect to thank-you page
    const handleCheckoutComplete = useCallback((eventData) => {
        const items = eventData?.items || [];
        const firstItem = items[0];
        const productId = firstItem?.product?.id;
        const productName = productId ? getProductNameFromPaddleId(productId) : 'rso';
        navigate(`/thank-you?product=${productName || 'rso'}`);
    }, [navigate]);

    // Load and initialize Paddle.js
    useEffect(() => {
        const config = getPaddleConfig();

        // Don't load if no token configured
        if (!config.token) {
            console.warn('Paddle client token not configured');
            return;
        }

        const initPaddle = () => {
            if (window.Paddle) {
                window.Paddle.Environment.set(config.environment);
                window.Paddle.Initialize({
                    token: config.token,
                    eventCallback: (event) => {
                        if (event.name === 'checkout.completed') {
                            handleCheckoutComplete(event.data);
                        }
                    },
                });
                setPaddleReady(true);
                console.log(`Paddle initialized in ${config.environment} mode`);

                // Fetch localized price with automatic tax-inclusive/exclusive based on region
                const priceId = getRsoPriceId();
                if (priceId) {
                    window.Paddle.PricePreview({
                        items: [{ priceId, quantity: 1 }]
                    }).then((result) => {
                        const data = result?.data;
                        const lineItem = data?.details?.lineItems?.[0];
                        if (lineItem?.formattedTotals) {
                            // Regions where prices are typically shown tax-inclusive
                            const taxInclusiveCountries = [
                                'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
                                'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
                                'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU countries
                                'GB', 'NO', 'CH', 'AU', 'NZ' // Other tax-inclusive regions
                            ];
                            const customerCountry = data?.address?.countryCode;
                            const showTaxInclusive = taxInclusiveCountries.includes(customerCountry);

                            // Show total (tax-inclusive) for EU/etc, subtotal (tax-exclusive) for US/etc
                            const displayPrice = showTaxInclusive
                                ? lineItem.formattedTotals.total
                                : lineItem.formattedTotals.subtotal;

                            if (displayPrice) {
                                setFormattedPrice(displayPrice);
                            }
                        }
                    }).catch((err) => {
                        console.warn('Failed to fetch Paddle price preview:', err);
                    });
                }
            }
        };

        // Check if already loaded
        if (window.Paddle) {
            initPaddle();
            return;
        }

        // Load Paddle.js
        const script = document.createElement('script');
        script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
        script.async = true;
        script.onload = initPaddle;
        document.head.appendChild(script);
    }, [handleCheckoutComplete]);

    // Click-outside handler for demo dropdown (handles multiple dropdown instances)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is inside any dropdown (using data attribute)
            const isInsideDropdown = event.target.closest('[data-demo-dropdown]');
            if (!isInsideDropdown) {
                setDemoDropdownOpen(false);
            }
        };
        if (demoDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [demoDropdownOpen]);

    // Handle buy button click - opens Paddle checkout overlay
    const handleBuyClick = (e, scrollToBottom = false) => {
        e.preventDefault();
        if (!consentChecked) {
            setShowConsentError(true);
            // Scroll to the appropriate consent checkbox
            const targetRef = scrollToBottom ? consentRefBottom : consentRefTop;
            if (targetRef.current) {
                targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            // Reset error state after animation
            setTimeout(() => setShowConsentError(false), 1000);
            return;
        }

        // Open Paddle checkout
        const priceId = getRsoPriceId();
        if (!priceId) {
            console.error('RSO price ID not configured');
            return;
        }

        if (!paddleReady || !window.Paddle) {
            console.error('Paddle not ready');
            return;
        }

        try {
            window.Paddle.Checkout.open({
                items: [{ priceId, quantity: 1 }],
                settings: {
                    theme: 'dark'
                }
            });
        } catch (err) {
            console.error('Failed to open Paddle checkout:', err);
        }
    };

    // Handle demo download click - validates consent before opening dropdown
    const handleDemoClick = (e, scrollToBottom = false) => {
        e.preventDefault();
        if (!consentChecked) {
            setShowConsentError(true);
            const targetRef = scrollToBottom ? consentRefBottom : consentRefTop;
            if (targetRef.current) {
                targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setTimeout(() => setShowConsentError(false), 1000);
            return;
        }
        setDemoDropdownOpen(!demoDropdownOpen);
    };

    const handleToggle = (sectionName) => {
        setActiveSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionName)) {
                newSet.delete(sectionName);
            } else {
                newSet.add(sectionName);
            }
            return newSet;
        });
    };



    return (
        <ProjectWrapper>
            <PageMeta
                title="RSO Synthesizer"
                description="RSO - An expressive VST3 synthesizer with exotic oscillators, vintage-style filters, and deep MPE integration. For Windows and macOS. $20."
                canonicalPath="/projects/rso"
                image="/og-rso.png"
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": ["Product", "SoftwareApplication"],
                "name": "RSO Synthesizer",
                "description": "An expressive VST3 synthesizer with exotic oscillators, vintage-style filters, and deep MPE integration. Features Quaternion Phase oscillator, Super VPS engine, and comprehensive modulation system.",
                "brand": {
                    "@type": "Brand",
                    "name": "Lucas Perron"
                },
                "manufacturer": {
                    "@type": "Organization",
                    "name": "Lucas Perron",
                    "url": "https://lperron.com"
                },
                "image": "https://lperron.com/assets/RSO/img/heroShot.png",
                "category": "Software > Audio Software > Virtual Instruments",
                "offers": {
                    "@type": "Offer",
                    "url": "https://lperron.com/projects/rso",
                    "priceCurrency": "EUR",
                    "price": "20.00",
                    "itemCondition": "https://schema.org/NewCondition",
                    "availability": "https://schema.org/InStock",
                    "seller": {
                        "@type": "Organization",
                        "name": "Lucas Perron",
                        "url": "https://lperron.com"
                    }
                },
                "additionalProperty": [
                    {
                        "@type": "PropertyValue",
                        "name": "File Format",
                        "value": "VST3"
                    },
                    {
                        "@type": "PropertyValue",
                        "name": "Operating System",
                        "value": "Windows 10+, macOS Apple Silicon"
                    },
                    {
                        "@type": "PropertyValue",
                        "name": "MPE Support",
                        "value": "Full MPE Integration"
                    }
                ],
                "audience": {
                    "@type": "PeopleAudience",
                    "audienceType": "Musicians, Producers, Sound Designers"
                },
                "applicationCategory": "MultimediaApplication",
                "operatingSystem": "Windows 10+, macOS (Apple Silicon)",
                "softwareVersion": "1.0.0",
                "downloadUrl": "https://lperron.com/projects/rso"
            })}} />
            <HeroSection>
                <Logo src="/assets/RSO/img/LOGO.svg" alt="RSO" width="300" height="228" />
                <SubHeadline>
                    An instrument that responds rather than obeys. RSO combines exotic oscillators with familiar sculpting tools and a deep modulation system, all built for exploration, performance, and happy accidents.
                </SubHeadline>
                <FigureWrapper style={{ marginTop: '10px', marginBottom: '30px' }}>
                    {/* ResponsiveImage now SSG-optimized - renders full <picture> at build time */}
                    <ResponsiveImage
                        src="/assets/RSO/img/heroShot.png"
                        alt="Full RSO synthesizer user interface"
                        loading="eager"
                        fetchPriority="high"
                        sizes="(max-width: 600px) 90vw, 675px"
                        style={{ maxHeight: '400px', objectFit: 'contain', display: 'block' }}
                    />
                </FigureWrapper>

                <ButtonWrapper>
                    <DownloadDropdown data-demo-dropdown>
                        <DropdownTrigger
                            className="fixed-button"
                            $isOpen={demoDropdownOpen}
                            onClick={(e) => handleDemoClick(e, false)}
                        >
                            Download Demo <DropdownArrow $isOpen={demoDropdownOpen}>▼</DropdownArrow>
                        </DropdownTrigger>
                        <DropdownMenu $isOpen={demoDropdownOpen}>
                            <DropdownItem
                                href={demoDownloads.windows}
                                download
                                onClick={() => setDemoDropdownOpen(false)}
                            >
                                Windows Installer
                            </DropdownItem>
                            <DropdownItem
                                href={demoDownloads.macos}
                                download
                                onClick={() => setDemoDropdownOpen(false)}
                            >
                                macOS (Apple Silicon)
                            </DropdownItem>
                        </DropdownMenu>
                    </DownloadDropdown>
                    <StyledButton
                        className="fixed-button"
                        onClick={(e) => handleBuyClick(e, false)}
                    >
                        Buy RSO - {formattedPrice}
                    </StyledButton>
                </ButtonWrapper>

                {/* Consent checkbox - required for download and purchase */}
                <ConsentWrapper ref={consentRefTop} $error={showConsentError}>
                    <ConsentCheckbox
                        type="checkbox"
                        id="purchase-consent-top"
                        checked={consentChecked}
                        onChange={(e) => {
                            setConsentChecked(e.target.checked);
                            if (e.target.checked) setShowConsentError(false);
                        }}
                    />
                    <ConsentLabel htmlFor="purchase-consent-top" $error={showConsentError}>
                        I have read and accept the <Link to={rsoProduct.termsPage}>Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
                    </ConsentLabel>
                </ConsentWrapper>
            </HeroSection>

            <Section>
                <SectionTitle>Instrument // Dialogue // Discovery</SectionTitle>
                <SubHeadlineContainer style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    <p>RSO is a VST3 synthesizer <Highlight>built for dialogue</Highlight>. Unusual sound sources, stereo from the start, flow through <Highlight>familiar sculpting tools</Highlight>: filters, wavefolders, low-pass gates, etc. A powerful modulation system keeps things moving, and <Highlight>MPE integration</Highlight> means your performance feeds directly into the sound.</p>
                        <p>The goal is simple: an instrument that <Highlight>has a mind of its own</Highlight>. One that breaks your habits, rewards exploration, and pulls you somewhere you wouldn't go alone. Not a tool that obeys—a <Highlight>partner that responds</Highlight>.</p>
                </SubHeadlineContainer>
                <FigureWrapper style={{ maxWidth: '650', width: 'fit-content', margin: '0 auto'}}>
                    <BorderedLargeFigureImage
                        src="/assets/RSO/img/anim3.gif"
                        alt="Animated GIF of RSO's Quaternion Phase oscillator visualizer."
                        sizes="(max-width: 768px) 90vw, 220px"
                        loading="eager"
                        fetchPriority="high"
                    />
                    <FigureCaption>
                        An invitation to explore the unexpected. The Quaternion visualizer offers a hypnotic, visual echo of the sound – part of the dialogue, guiding discovery through intuition.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            <Section>
                <SectionTitle>THE RSO ARCHITECTURE</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto' }}>
                    Unconventional oscillators, familiar sculpting tools, and a modulation system that keeps the exchange alive. Three pillars, one principle: tension between the unexpected and the known.
                </SubHeadline>

                <ExpandableGrid>
                    {sectionOrder.map((sectionName, index) => {
                        const isActive = activeSections.has(sectionName);
                        const isLast = index === sectionOrder.length - 1;
                        
                        const getSectionData = (name) => {
                            switch(name) {
                                case 'osc':
                                    return {
                                        title: 'The Sound Sources',
                                        description: 'RSO offers two oscillator engines, both stereo, both exotic. The <Highlight>Quaternion Phase Oscillator</Highlight> creates complex (and sometimes unstable) textures through 4D chaotic math. The <Highlight>Super VPS</Highlight> stacks seven vector phaseshaping oscillators for rich harmonics. Forget the usual suspects, new territory awaits.',
                                        buttonText: isActive ? 'Hide Oscillator Details' : 'Explore the Oscillators',
                                        image: '/assets/RSO/img/scope2.png'
                                    };
                                case 'sculpting':
                                    return {
                                        title: 'The Sculpting Tools',
                                        description: 'Shape the oscillators with your choice of filter: a <Highlight>24dB ladder</Highlight>, a <Highlight>West Coast Channel</Highlight> with wavefolder and low-pass gate, or an <Highlight>XPander-style multimode</Highlight>. The signal path ends with a flexible <Highlight>ADSR envelope</Highlight> and stereo output stage. Coming soon: Polyvoks Filter.',
                                        buttonText: isActive ? 'Hide Sculpting Details' : 'Explore the Sculpting Tools',
                                        image: '/assets/RSO/img/curves.gif'
                                    };
                                case 'animation':
                                    return {
                                        title: 'The Modulation Engine',
                                        description: '<Highlight>Every parameter</Highlight> in RSO can be modulated through a powerful and versatile system. <Highlight>MPE integration</Highlight>, tempo sync, and a range of <Highlight>creative modulators</Highlight>: morphing LFOs, generative noise surveyors, function generators. <Highlight>Visual, intuitive, hands-on</Highlight>.',
                                        buttonText: isActive ? 'Hide Modulation Details' : 'Explore the Modulation Engine',
                                        image: '/assets/RSO/img/modGrid.png'
                                    };
                                default:
                                    return { title: '', description: '', buttonText: '', image: '' };
                            }
                        };
                        
                        const sectionData = getSectionData(sectionName);
                        
                        return (
                            <AccordionSection
                                key={sectionName}
                                $isExpanded={isActive}
                                $isLast={isLast}
                            >
                                <FeatureItem
                                    as="button"
                                    type="button"
                                    $isActive={isActive}
                                    onClick={() => handleToggle(sectionName)}
                                    aria-expanded={isActive}
                                    aria-controls={`section-details-${sectionName}`}
                                    data-feature-item
                                >
                                    <FeatureTitle>{sectionData.title}</FeatureTitle>
                                    <FeatureContentLayout>
                                        <ThumbnailWrapper $fullWidth={sectionName === 'sculpting'}>
                                            <FeatureThumbnail
                                                src={sectionData.image}
                                                alt={sectionData.title}
                                                $fullWidth={sectionName === 'sculpting'}
                                                sizes="120px"
                                                preventNavigation={true}
                                            />
                                        </ThumbnailWrapper>
                                        <FeatureTextContent>
                                            <FeatureList>
                                                <FeatureListItem className="feature-list-item">
                                                    {parseDescription(sectionData.description)}
                                                </FeatureListItem>
                                            </FeatureList>
                                        </FeatureTextContent>
                                    </FeatureContentLayout>
                                    <SummaryButton as="div">
                                        {sectionData.buttonText}
                                    </SummaryButton>
                                </FeatureItem>
                                
                                <DetailsContainer
                                    id={`section-details-${sectionName}`}
                                    height={isActive ? 1 : 0}
                                    $isLast={isLast}
                                    data-details-container
                                >
                                    {isActive && (
                                        <div>
                                            <StickyTitleContainer>
                                                {sectionData.title}
                                            </StickyTitleContainer>
                                            <ContentWrapper isVisible={isActive}>
                                                <SectionContent sectionName={sectionName} />
                                            </ContentWrapper>
                                        </div>
                                    )}
                                </DetailsContainer>
                            </AccordionSection>
                        );
                    })}
                </ExpandableGrid>
            </Section>

            <Section>
                <AsideLink>
                    Want to go deeper? <a href={manualUrl} target="_blank" rel="noopener noreferrer">Take a look at the User Manual</a>.
                </AsideLink>
            </Section>

            <Section>
                <SectionTitle>Sonic Identity</SectionTitle>
                                                        <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                                                            RSO shines with <Highlight>evolving pads, complex textures, and expressive performances</Highlight>—but it's not limited to atmospherics. <Highlight>Solid basses, sharp plucks, rich keys</Highlight> are all in there. The modulation system and MPE support mean everything can <Highlight>move, breathe, respond to your touch</Highlight>. Don't take our word for it—here's what it sounds like:
                                                        </SubHeadline>
                                                        <FigureWrapper>
                                                            <AudioPlayer tracks={rsoAudioTracks} />
                                                            <FigureCaption>
                                                                Audio demos created exclusively with RSO (plus external FX).
                                                            </FigureCaption>
                                                        </FigureWrapper>
                                                    </Section>            <Section>
                <SectionTitle>Interface & Interaction</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    The RSO interface is <Highlight>flat, clean, and focused</Highlight>, prioritizing sound and playability. Every control is right there, no menu diving required. <Highlight>Visual feedback</Highlight> where it matters, no clutter where it doesn't. And with the <Highlight>Theming System</Highlight>, you can choose from several included themes or create your own.</SubHeadline>
                <FigureWrapper>
                    <LargeFigureImage
                        src="/assets/RSO/img/Themes.png"
                        alt="Comparison of different visual themes available in RSO, showcasing the customizable color schemes and interface styles."
                        sizes="(max-width: 800px) 90vw, 800px"
                    />
                    <FigureCaption>
                        Choose your aesthetic: customize RSO's visual identity to match your workflow.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            <Section>
                <SectionTitle>Who is RSO For?</SectionTitle>
                <AudienceList style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <AudienceItem><strong>Electronic Music Producers:</strong> Especially those in ambient, IDM, or experimental genres seeking unique, evolving textures.</AudienceItem>
                    <AudienceItem><strong>Sound Designers:</strong> Creators looking for unusual textures and atmospheric layers for film, games, or music.</AudienceItem>
                    <AudienceItem><strong>Expressive Performers & MPE Enthusiasts:</strong> Players who want to truly connect with their instrument and get deep expressive control through MPE.</AudienceItem>
                    <AudienceItem><strong>Synth Enthusiasts:</strong> Anyone looking to explore rare synthesis and modulation methods within a cohesive, playful environment.</AudienceItem>
                    <AudienceItem><strong>Musicians Seeking Inspiration:</strong> Break creative blocks with an instrument that rewards experimentation and happy accidents.</AudienceItem>
                </AudienceList>
            </Section>

            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>SYSTEM SPECIFICATIONS</SectionTitle>
                <TechSpecsList>
                    <TechSpecItem><span>Format</span><span>VST3</span></TechSpecItem>
                    <TechSpecItem><span>Operating System</span><span>Windows & macOS (Apple Silicon)</span></TechSpecItem>
                    <TechSpecItem><span>MPE Support</span><span>Full MPE Integration</span></TechSpecItem>
                    <TechSpecItem><span>Linux</span><span>Not currently available</span></TechSpecItem>
                    <TechSpecItem><span>Manual</span><span><a href={manualUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', transition: 'color 0.3s ease' }} onMouseOver={(e) => e.currentTarget.style.color = 'var(--link-hover-color)'} onMouseOut={(e) => e.currentTarget.style.color = 'inherit'}>Download PDF</a></span></TechSpecItem>
                </TechSpecsList>
            </Section>

            <Section>
                <SectionTitle>A Living Project</SectionTitle>
                <SubHeadlineContainer style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    <p>RSO is <Highlight>actively evolving</Highlight>. New factory presets and themes are on the way, along with ongoing refinements based on real-world use. Further ahead: <Highlight>new oscillators, filters, and modulation sources</Highlight>.</p>
                    <p>If you're interested in shaping that evolution, we're looking for <Highlight>beta testers</Highlight>. You'll get early access to new features, and your feedback will directly influence what RSO becomes.</p>
                </SubHeadlineContainer>
                <ButtonWrapper>
                    <StyledButton
                        onClick={() => window.location.href = `mailto:${email}?subject=RSO Beta Access Request`}
                    >
                        Become a Tester
                    </StyledButton>
                </ButtonWrapper>
            </Section>

            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Dive In</SectionTitle>
                <SubHeadline>
                    Ready to explore? The demo gives you the <Highlight>full synth</Highlight> with no sound restrictions and no time limit. Only preset saving and DAW state recall are disabled. Try it for <Highlight>free</Highlight>, or purchase for full access.
                </SubHeadline>
                <ButtonWrapper>
                    <DownloadDropdown data-demo-dropdown>
                        <DropdownTrigger
                            className="fixed-button"
                            $isOpen={demoDropdownOpen}
                            onClick={(e) => handleDemoClick(e, true)}
                        >
                            Download Demo <DropdownArrow $isOpen={demoDropdownOpen}>▼</DropdownArrow>
                        </DropdownTrigger>
                        <DropdownMenu $isOpen={demoDropdownOpen}>
                            <DropdownItem
                                href={demoDownloads.windows}
                                download
                                onClick={() => setDemoDropdownOpen(false)}
                            >
                                Windows Installer
                            </DropdownItem>
                            <DropdownItem
                                href={demoDownloads.macos}
                                download
                                onClick={() => setDemoDropdownOpen(false)}
                            >
                                macOS (Apple Silicon)
                            </DropdownItem>
                        </DropdownMenu>
                    </DownloadDropdown>
                    <StyledButton
                        className="fixed-button"
                        onClick={(e) => handleBuyClick(e, true)}
                    >
                        Buy RSO - {formattedPrice}
                    </StyledButton>
                </ButtonWrapper>

                {/* Consent checkbox - required for download and purchase */}
                <ConsentWrapper ref={consentRefBottom} $error={showConsentError}>
                    <ConsentCheckbox
                        type="checkbox"
                        id="purchase-consent-bottom"
                        checked={consentChecked}
                        onChange={(e) => {
                            setConsentChecked(e.target.checked);
                            if (e.target.checked) setShowConsentError(false);
                        }}
                    />
                    <ConsentLabel htmlFor="purchase-consent-bottom" $error={showConsentError}>
                        I have read and accept the <Link to={rsoProduct.termsPage}>Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
                    </ConsentLabel>
                </ConsentWrapper>

                {/* Demo note */}
                <PurchaseDisclaimer style={{ marginTop: '30px' }}>
                    Even if the demo doesn't save presets or recall DAW state, your DAW stores RSO's data internally. If you activate RSO later, everything you created in demo mode can be recovered. For details, see section 1.c of the <a href={manualUrl} target="_blank" rel="noopener noreferrer">user manual</a>.
                </PurchaseDisclaimer>
            </Section>

        </ProjectWrapper>
    );
};

export default RsoPage;

